const path = (key, suffix = '') => `/v1/config/settings/${encodeURIComponent(key)}${suffix}`;
export class SettingsApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /** The registry: every governed key with each bucket's governing version, waiting version and pending changes. */
    list() {
        return this.client.request({ method: 'GET', path: '/v1/config/settings', auth: true });
    }
    /** One key: its definition, every bucket, every admissible version, the checker queue and the attestations. */
    get(key, target) {
        // D854 (L6b-001): a target narrows the answer to that bucket's chain (market -> state -> country -> platform).
        const params = new URLSearchParams();
        if (target !== undefined) {
            params.set('scope', target.scope);
            if (target.scopeRef !== undefined && target.scopeRef !== '')
                params.set('scopeRef', target.scopeRef);
        }
        const qs = params.toString();
        return this.client.request({ method: 'GET', path: path(key, qs !== '' ? `?${qs}` : ''), auth: true });
    }
    /** One key's whole audit timeline (every revision, oldest first), optionally narrowed to one bucket. */
    history(key, target) {
        const params = new URLSearchParams();
        if (target !== undefined) {
            params.set('scope', target.scope);
            if (target.scopeRef !== undefined && target.scopeRef !== '')
                params.set('scopeRef', target.scopeRef);
        }
        const qs = params.toString();
        return this.client.request({
            method: 'GET', path: path(key, `/history${qs !== '' ? `?${qs}` : ''}`), auth: true,
        });
    }
    /** The value of a key in force at a market, a state, a country or the platform, with the chain of layers it came through. */
    effective(key, query = {}) {
        const params = new URLSearchParams();
        if (query.market !== undefined)
            params.set('market', query.market);
        if (query.region !== undefined)
            params.set('region', query.region);
        if (query.country !== undefined)
            params.set('country', query.country);
        const qs = params.toString();
        return this.client.request({ method: 'GET', path: path(key, `/effective${qs !== '' ? `?${qs}` : ''}`), auth: true });
    }
    /** Validate a DRAFT value for a bucket and diff it against what governs. Writes nothing. */
    preview(key, input) {
        return this.client.request({ method: 'POST', path: path(key, '/preview'), body: input, auth: true });
    }
    /** Propose a new value for a bucket (the maker). Held for a DIFFERENT operator; one pending change per bucket. */
    propose(key, input) {
        return this.client.request({ method: 'POST', path: path(key, '/changes'), body: input, auth: true });
    }
    /** Approve a pending change (the checker). Publishing the version is what approval does. */
    approve(key, changeId, input) {
        return this.client.request({ method: 'POST', path: path(key, `/changes/${encodeURIComponent(changeId)}/approve`), body: input, auth: true });
    }
    /** Reject a pending change (the checker). Nothing is published; the request stays on the record. */
    reject(key, changeId, input) {
        return this.client.request({ method: 'POST', path: path(key, `/changes/${encodeURIComponent(changeId)}/reject`), body: input, auth: true });
    }
    /** Withdraw a pending change — its requester only (`NOT_REQUESTER` otherwise). */
    withdraw(key, changeId, input) {
        return this.client.request({ method: 'POST', path: path(key, `/changes/${encodeURIComponent(changeId)}/withdraw`), body: input, auth: true });
    }
    /**
     * Restore an earlier version as a NEW version copying it, pinned by that version's content hash. One step back
     * applies at once and needs {@link reviewRollback} afterwards; any other target is an ordinary four-eyes change.
     */
    rollback(key, input) {
        return this.client.request({ method: 'POST', path: path(key, '/rollback'), body: input, auth: true });
    }
    /**
     * D856 (LT-002 / L6b-018): propose that a country, state or market bucket hold NO value any more, so the next broader scope
     * governs again. Held for a DIFFERENT operator, who approves it with {@link approve}; approving publishes a retired version.
     * `NOTHING_TO_END` while the bucket has no value of its own, `PLATFORM_CANNOT_INHERIT` for the platform bucket.
     */
    end(key, input) {
        return this.client.request({ method: 'POST', path: path(key, '/end'), body: input, auth: true });
    }
    /** The post-review of a one-step rollback, by a DIFFERENT operator (`SELF_REVIEW` otherwise). */
    reviewRollback(key, changeId, input) {
        return this.client.request({ method: 'POST', path: path(key, `/changes/${encodeURIComponent(changeId)}/review`), body: input, auth: true });
    }
}
//# sourceMappingURL=settings.js.map